import { useTranslation } from 'next-i18next';
import React, { useMemo } from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import styled from 'styled-components';
import { OrlFlightAlternative, TravelType } from '@hotelplan/graphql.types';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import { useSearchState } from '@hotelplan/libs.search-state';
import { sx2CssThemeFn } from '@hotelplan/libs.sx';
import OfferItem from 'components/domain/orl/offerItem/OfferItem';
import { mapSearchControlRoomsToOfferRooms } from 'components/domain/orl/Orl.mappers';
import { ICheckoutOffer, IOrlState } from 'components/domain/orl/Orl.types';
import {
  ORL_SORTING_LABEL_KEYS,
  useORLSortingContext,
} from 'components/domain/orl/orlResults/ORLSorting';
import { OrlItemFragment } from 'graphql/fragments/ORLItem.generated';
import {
  ETrackingOfferPosition,
  trackOfferClick,
} from 'libs/tracking/trackingEvents/trackSelectOffer';

const OrlResultsWrap = styled.div<{ hasAlternativeFlights?: boolean }>(
  ({ hasAlternativeFlights }) =>
    sx2CssThemeFn({
      display: 'block',
      '.header': {
        display: 'grid',
        gridTemplateColumns: hasAlternativeFlights
          ? '15% 26% 24% 18% 18%'
          : '27% 23% 30% 20%',
        fontWeight: 'bold',
        p: '5px 10px 20px',
        '.title': {
          fontSize: 2,
        },
        '.subtitle': {
          fontSize: '14px',
        },
        '.price .title': {
          color: 'secondary',
        },
      },
      '.offer-item:nth-child(odd)': {
        backgroundColor: [null, 'backgroundGray'],
      },
    })
);

interface IOrlResults {
  offers: OrlItemFragment[];
  onShowMore: (nextPage: number | null) => void;
  possibleNextPage: number;
  onCheckout(
    offer: ICheckoutOffer,
    id: string,
    isTrackECommerce?: boolean
  ): (alternativeFlight?: OrlFlightAlternative) => void;
}

function OrlResults({
  offers,
  onShowMore,
  possibleNextPage,
  onCheckout,
}: IOrlResults) {
  const [t, { language }] = useTranslation(['common', 'orl']);
  const { mobile } = useDeviceType();
  const { id } = useObjectId();

  const {
    state: { searchControl },
  } = useSearchState<IOrlState>();
  const { sortingValue } = useORLSortingContext();

  const travellersCount = useMemo(() => {
    return mapSearchControlRoomsToOfferRooms(searchControl?.travelRooms);
  }, [searchControl?.travelRooms]);

  const hasAlternativeFlights =
    searchControl?.travelType === TravelType.Package;

  return (
    <OrlResultsWrap hasAlternativeFlights={hasAlternativeFlights}>
      {!mobile ? (
        <div className="header">
          <div className="date">
            <h3 className="title">{t('orl:date.title')}</h3>
            <div className="subtitle">{t('orl:date.subtitle')}</div>
          </div>
          <div className="airport">
            <h3 className="title">
              {hasAlternativeFlights
                ? t('orl:departureAirport.title')
                : t('orl:sort.duration')}
            </h3>
            {hasAlternativeFlights ? (
              <div className="subtitle">
                {t('orl:departureAirport.subtitle')}
              </div>
            ) : null}
          </div>
          <div className="room-type">
            <h3 className="title">{t('orl:roomType.title')}</h3>
            <div className="subtitle">{t('orl:roomType.subtitle')}</div>
          </div>
          {hasAlternativeFlights ? (
            <div className="flight">
              <h3 className="title">{t('orl:flight.title')}</h3>
              <div className="subtitle">{t('orl:flight.subtitle')}</div>
            </div>
          ) : null}
          <div className="price">
            <h3 className="title">{t('orl:totalPrice.title')}</h3>
            <div className="subtitle">
              {t('common:adult.count', { count: travellersCount.adults })}
            </div>
            {travellersCount.children ? (
              <div className="subtitle">
                {t('common:child.count', { count: travellersCount.children })}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {offers?.map((offer, offerIndex) => {
        const isLastItem = offerIndex === offers.length - 1;

        return (
          <VisibilitySensor
            partialVisibility
            key={offer.id}
            active={isLastItem}
            onChange={isVisible => {
              const isLatestVisibleItem = isVisible && isLastItem;
              if (isLatestVisibleItem) {
                onShowMore(possibleNextPage);
              }
            }}
          >
            <OfferItem
              offer={offer}
              key={offer.id}
              onCheckout={(alternativeFlight, flightIndex) => {
                trackOfferClick(language, {
                  position: ETrackingOfferPosition.ORL,
                  offer_position: flightIndex
                    ? flightIndex + 1
                    : offerIndex + 1,
                  select_offer_page: flightIndex ? offerIndex + 1 : 0,
                  sortingLabel: t(ORL_SORTING_LABEL_KEYS[sortingValue], {
                    lng: 'de',
                  }),
                });
                onCheckout(offer, id, true)(alternativeFlight);
              }}
            />
          </VisibilitySensor>
        );
      })}
    </OrlResultsWrap>
  );
}

export default OrlResults;
